// Income — what Rootegra earned this year by account, and a form for income
// that does not come from Apple (workshops, Pro subscriptions, anything else).
import { useEffect, useMemo, useState } from 'react';
import { useBooksAccess } from '../../contexts/AuthContext';
import { Empty, ErrorNote, Field, Note, Section } from '../../components/ui';
import { day, dollars } from '../../lib/format';
import { journal, reports } from '../../lib/ledger/api';
import type { Account, JournalEntry } from '../../lib/ledger/types';
import { buildProfitLoss } from '../../lib/ledger/reports';
import { AccountSelect, Money, MoneyInput, ReadOnlyNote, downloadCsv, today, useAccounts, yearStart } from './shared';

function useLoad<T>(fn: () => Promise<T>, deps: readonly unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => { if (alive) setData(d); })
      .catch((e: unknown) => { if (alive) setError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, error, loading, reload: () => setTick((t) => t + 1) };
}

/** Where income can land: cash-like assets and amounts owed to Rootegra. */
function depositCandidates(accounts: Account[]): Account[] {
  return accounts.filter((a) => a.type === 'asset' && ['bank', 'vessel', 'accounts_receivable'].includes(a.subtype));
}

function entryParts(e: JournalEntry) {
  const credit = e.lines.find((l) => Number(l.credit_cents) > 0);
  const debit = e.lines.find((l) => Number(l.debit_cents) > 0);
  const amount = e.lines.reduce((s, l) => s + Number(l.credit_cents), 0);
  return { credit, debit, amount };
}

export function IncomeTab() {
  const { canEdit } = useBooksAccess();
  const chart = useAccounts();
  const from = yearStart();
  const to = today();
  const year = from.slice(0, 4);
  const tb = useLoad(() => reports.trialBalance(from, to), [from, to]);
  const entries = useLoad(() => journal.page({ from, to, source: 'income', limit: 500 }), [from, to]);
  const pl = useMemo(() => (tb.data ? buildProfitLoss(tb.data) : null), [tb.data]);

  const [form, setForm] = useState({ date: today(), amount: null as number | null, income_account_id: '', deposit_account_id: '', source_label: '', memo: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);

  const ready = form.date && form.amount != null && form.amount > 0 && form.income_account_id && form.deposit_account_id && form.source_label.trim();

  async function record() {
    if (!ready || form.amount == null) return;
    setBusy(true); setErr(null); setDone(null);
    try {
      await journal.recordIncome({
        date: form.date, amount_cents: form.amount, income_account_id: form.income_account_id,
        deposit_account_id: form.deposit_account_id, memo: form.memo.trim(), source_label: form.source_label.trim(),
      });
      setDone(`Recorded ${dollars(form.amount)} from ${form.source_label.trim()} to ${chart.byId.get(form.income_account_id)?.name ?? 'income'}.`);
      setForm({ ...form, amount: null, source_label: '', memo: '' });
      tb.reload();
      entries.reload();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  }

  const list = entries.data?.entries ?? [];
  const exportCsv = () => {
    downloadCsv(`rootegra-income-${year}.csv`, [
      ['Date', 'Source', 'Memo', 'Income account', 'Deposited to', 'Amount'],
      ...list.map((e) => {
        const p = entryParts(e);
        return [e.entry_date, String(e.details?.source_label ?? ''), e.memo ?? '', p.credit ? `${p.credit.code ?? ''} ${p.credit.name ?? ''}`.trim() : '', p.debit ? `${p.debit.code ?? ''} ${p.debit.name ?? ''}`.trim() : '', (p.amount / 100).toFixed(2)];
      }),
    ]);
  };

  return (
    <>
      {!canEdit && <ReadOnlyNote />}
      <ErrorNote error={chart.error} />
      <ErrorNote error={tb.error} />
      <div className="kpi-grid">
        <div className="kpi"><div className="kpi-label">Income, {year} to date</div><div className="kpi-num">{pl ? dollars(pl.income.total_cents) : '—'}</div><div className="kpi-sub">Net of refunds</div></div>
        <div className="kpi"><div className="kpi-label">Cost of goods sold</div><div className="kpi-num">{pl ? dollars(pl.cogs.total_cents) : '—'}</div><div className="kpi-sub">Course production</div></div>
        <div className="kpi"><div className="kpi-label">Gross profit</div><div className="kpi-num">{pl ? dollars(pl.gross_profit_cents) : '—'}</div><div className="kpi-sub">Income minus cost of goods</div></div>
      </div>

      <Section title={`By account, ${year}`}>
        <Note>Apple income posts itself when a month closes on the Apple tab: gross sales, Apple's commission and refunds each land in their own account. Record everything else here.</Note>
        {tb.loading ? <Empty>Loading…</Empty> : !pl || (pl.income.rows.length === 0 && pl.cogs.rows.length === 0) ? <Empty>No income recorded yet this year.</Empty> : (
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead><tr><th>Account</th><th className="num">{year} to date</th></tr></thead>
              <tbody>
                {pl.income.rows.map((r) => <tr key={r.account_id}><td>{r.code} · {r.name}</td><td className="num"><Money cents={r.amount_cents} /></td></tr>)}
                <tr className="total"><td>Total income</td><td className="num"><Money cents={pl.income.total_cents} /></td></tr>
                {pl.cogs.rows.map((r) => <tr key={r.account_id}><td>{r.code} · {r.name}</td><td className="num"><Money cents={-r.amount_cents} /></td></tr>)}
                {pl.cogs.rows.length > 0 && <tr className="total"><td>Gross profit</td><td className="num"><Money cents={pl.gross_profit_cents} /></td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Record income">
        <ErrorNote error={err} />
        {done && <Note tone="good">{done}</Note>}
        <div className="lp-form-grid">
          <Field label="Date"><input className="lp-inp" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} disabled={!canEdit} /></Field>
          <Field label="Amount"><MoneyInput cents={form.amount} onChange={(c) => setForm({ ...form, amount: c })} disabled={!canEdit} /></Field>
          <Field label="Income account"><AccountSelect value={form.income_account_id} onChange={(id) => setForm({ ...form, income_account_id: id })} accounts={chart.accounts} types={['income']} disabled={!canEdit} /></Field>
          <Field label="Deposited to"><AccountSelect value={form.deposit_account_id} onChange={(id) => setForm({ ...form, deposit_account_id: id })} accounts={depositCandidates(chart.accounts)} disabled={!canEdit} /></Field>
          <Field label="Source" hint="Who paid, or what for."><input className="lp-inp" value={form.source_label} onChange={(e) => setForm({ ...form, source_label: e.target.value })} disabled={!canEdit} placeholder="January extensions workshop" /></Field>
          <Field label="Memo"><input className="lp-inp" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} disabled={!canEdit} placeholder="Optional" /></Field>
        </div>
        <div className="lp-row">
          <button type="button" className="lp-btn lp-btn--primary" disabled={!canEdit || busy || !ready} onClick={() => void record()}>{busy ? 'Recording…' : 'Record income'}</button>
        </div>
      </Section>

      <Section title="Recorded this year" right={<button type="button" className="lp-btn lp-btn--quiet lp-no-print" disabled={list.length === 0} onClick={exportCsv}>CSV</button>}>
        <ErrorNote error={entries.error} />
        {entries.loading ? <Empty>Loading…</Empty> : list.length === 0 ? <Empty>Nothing recorded by hand this year. Apple's months show under Journal.</Empty> : (
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead><tr><th>Date</th><th>Source</th><th>Memo</th><th>Income account</th><th>Deposited to</th><th className="num">Amount</th></tr></thead>
              <tbody>
                {list.map((e) => {
                  const p = entryParts(e);
                  return (
                    <tr key={e.id} className={e.is_reversed ? 'quiet' : undefined}>
                      <td>{day(e.entry_date)}</td>
                      <td>{String(e.details?.source_label ?? '') || '—'}{e.is_reversed && <span className="lp-tag lp-tag--warn" style={{ marginLeft: 6 }}>Reversed</span>}</td>
                      <td>{e.memo || '—'}</td>
                      <td>{p.credit ? `${p.credit.code ?? ''} · ${p.credit.name ?? chart.byId.get(p.credit.account_id)?.name ?? ''}` : '—'}</td>
                      <td>{p.debit ? `${p.debit.code ?? ''} · ${p.debit.name ?? chart.byId.get(p.debit.account_id)?.name ?? ''}` : '—'}</td>
                      <td className="num"><Money cents={p.amount} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </>
  );
}
